import React, { useState, useEffect, useCallback } from 'react';
import ExcelJS from 'exceljs';
import { reportesAPI, clientesAPI, authAPI } from '../services/api';
import { getCurrencyConfig, formatMoney } from '../utils/currency';
import './Reportes.css';

function Reportes() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState(null); // Inicializar como null para determinar automáticamente
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [reporte, setReporte] = useState(null);
  const currency = getCurrencyConfig(user);
  const money = (value) => formatMoney(value, { ...currency, decimals: 2 });
  
  const [filtrosFacturacion, setFiltrosFacturacion] = useState({
    fecha_desde: '',
    fecha_hasta: '',
    cliente_id: '',
  });
  
  const [filtrosOportunidades, setFiltrosOportunidades] = useState({
    fecha_desde: '',
    fecha_hasta: '',
    estado: '',
  });

  useEffect(() => {
    // Cargar información del usuario
    authAPI.check()
      .then(response => {
        if (response.data.authenticated && response.data.user) {
          setUser(response.data.user);
          // Determinar la pestaña inicial según los permisos
          const facturacionHabilitada = response.data.user.facturacion_habilitada === true || response.data.user.facturacion_habilitada === 1;
          const oportunidadesHabilitadas = response.data.user.oportunidades_habilitadas === true || response.data.user.oportunidades_habilitadas === 1;
          
          if (facturacionHabilitada && !tab) {
            setTab('facturacion');
          } else if (oportunidadesHabilitadas && !tab) {
            setTab('oportunidades');
          }
        }
      })
      .catch(err => {
        console.error('Error al verificar usuario:', err);
      });
    
    loadClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClientes = async () => {
    try {
      const r = await clientesAPI.getAll(true);
      setClientes(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setClientes([]);
    }
  };

  const loadReporteFacturacion = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (filtrosFacturacion.fecha_desde) filters.fecha_desde = filtrosFacturacion.fecha_desde;
      if (filtrosFacturacion.fecha_hasta) filters.fecha_hasta = filtrosFacturacion.fecha_hasta;
      if (filtrosFacturacion.cliente_id) filters.cliente_id = filtrosFacturacion.cliente_id;
      
      const r = await reportesAPI.getFacturacion(filters);
      setReporte(r.data?.data ?? r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar reporte');
      setReporte(null);
    } finally {
      setLoading(false);
    }
  }, [filtrosFacturacion]);

  const loadReporteOportunidades = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (filtrosOportunidades.fecha_desde) filters.fecha_desde = filtrosOportunidades.fecha_desde;
      if (filtrosOportunidades.fecha_hasta) filters.fecha_hasta = filtrosOportunidades.fecha_hasta;
      if (filtrosOportunidades.estado) filters.estado = filtrosOportunidades.estado;
      
      const r = await reportesAPI.getOportunidades(filters);
      setReporte(r.data?.data ?? r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar reporte');
      setReporte(null);
    } finally {
      setLoading(false);
    }
  }, [filtrosOportunidades]);

  useEffect(() => {
    if (!tab) return;
    if (tab === 'facturacion') {
      loadReporteFacturacion();
    } else if (tab === 'oportunidades') {
      loadReporteOportunidades();
    }
  }, [tab, loadReporteFacturacion, loadReporteOportunidades]);

  const handleExportCSV = () => {
    if (tab === 'facturacion') {
      reportesAPI.exportCSV('facturacion', filtrosFacturacion);
    } else {
      reportesAPI.exportCSV('oportunidades', filtrosOportunidades);
    }
  };

  const handleExportExcel = async () => {
    if (!reporte) return;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'CoachSara CRM';
    const fecha = new Date().toISOString().slice(0, 10);
    if (tab === 'facturacion') {
      const ws = wb.addWorksheet('Facturación', { views: [{ state: 'frozen', ySplit: 1 }] });
      const headers = ['Número', 'Fecha Emisión', 'Fecha Vencimiento', 'Cliente', 'Base Imponible', 'IVA', 'Total', 'Estado'];
      const headerRow = ws.addRow(headers);
      headerRow.font = { bold: true };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2563EB' } };
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      (reporte.facturas || []).forEach((f) => {
        ws.addRow([
          f.numero ?? '',
          f.fecha_emision ?? '',
          f.fecha_vencimiento ?? '',
          `${f.cliente_nombre ?? ''} ${f.cliente_apellidos ?? ''}`.trim(),
          Number(f.base_imponible ?? 0),
          Number(f.importe_iva ?? 0),
          Number(f.total ?? 0),
          f.estado ?? '',
        ]);
      });
      if (reporte.totales) {
        ws.addRow([]);
        ws.addRow(['Total facturado', '', '', '', reporte.totales.total_base ?? 0, reporte.totales.total_iva ?? 0, reporte.totales.total_facturado ?? 0, '']);
        ws.lastRow.font = { bold: true };
      }
      ws.getColumn(5).numFmt = '#,##0.00';
      ws.getColumn(6).numFmt = '#,##0.00';
      ws.getColumn(7).numFmt = '#,##0.00';
      ws.columns = [{ width: 12 }, { width: 14 }, { width: 14 }, { width: 28 }, { width: 14 }, { width: 12 }, { width: 14 }, { width: 12 }];
    } else {
      const ws = wb.addWorksheet('Oportunidades', { views: [{ state: 'frozen', ySplit: 1 }] });
      const headers = ['Título', 'Cliente', 'Valor Estimado', 'Probabilidad %', 'Valor Ponderado', 'Etapa', 'Prioridad', 'Fecha Cierre'];
      const headerRow = ws.addRow(headers);
      headerRow.font = { bold: true };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2563EB' } };
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      (reporte.oportunidades || []).forEach((o) => {
        ws.addRow([
          o.titulo ?? '',
          `${o.cliente_nombre ?? ''} ${o.cliente_apellidos ?? ''}`.trim(),
          Number(o.valor_estimado ?? 0),
          Number(o.probabilidad ?? 0),
          Number(o.valor_ponderado ?? 0),
          o.etapa ?? '',
          o.prioridad ?? '',
          o.fecha_estimada_cierre ?? '',
        ]);
      });
      if (reporte.metricas) {
        ws.addRow([]);
        ws.addRow(['Valor total', '', reporte.metricas.valor_total ?? 0, '', reporte.metricas.valor_ponderado ?? 0, '', '', '']);
        ws.lastRow.font = { bold: true };
      }
      ws.getColumn(3).numFmt = '#,##0.00';
      ws.getColumn(5).numFmt = '#,##0.00';
      ws.columns = [{ width: 28 }, { width: 22 }, { width: 14 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 10 }, { width: 14 }];
    }
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `reporte_${tab}_${fecha}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="reportes-page">
      <div className="page-header">
        <h2>Reportes y Análisis</h2>
        {reporte && (
          <div className="reportes-export-buttons" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" onClick={handleExportCSV}>
              📥 Exportar CSV
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleExportExcel}>
              📊 Exportar Excel
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => tab === 'facturacion' ? reportesAPI.exportPDF('facturacion', filtrosFacturacion) : reportesAPI.exportPDF('oportunidades', filtrosOportunidades)}>
              📄 Exportar PDF
            </button>
          </div>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {user && (
        <div className="reportes-tabs">
          {(user.facturacion_habilitada === true || user.facturacion_habilitada === 1) && (
            <button type="button" className={tab === 'facturacion' ? 'active' : ''} onClick={() => setTab('facturacion')}>
              Facturación
            </button>
          )}
          {(user.oportunidades_habilitadas === true || user.oportunidades_habilitadas === 1) && (
            <button type="button" className={tab === 'oportunidades' ? 'active' : ''} onClick={() => setTab('oportunidades')}>
              Oportunidades
            </button>
          )}
        </div>
      )}

      {tab === 'facturacion' && (
        <div className="reportes-filters">
          <div className="filters-row">
            <div className="filter-group">
              <label>Fecha desde</label>
              <input 
                type="date" 
                value={filtrosFacturacion.fecha_desde} 
                onChange={(e) => setFiltrosFacturacion((f) => ({ ...f, fecha_desde: e.target.value }))} 
              />
            </div>
            <div className="filter-group">
              <label>Fecha hasta</label>
              <input 
                type="date" 
                value={filtrosFacturacion.fecha_hasta} 
                onChange={(e) => setFiltrosFacturacion((f) => ({ ...f, fecha_hasta: e.target.value }))} 
              />
            </div>
            <div className="filter-group">
              <label>Cliente</label>
              <select 
                value={filtrosFacturacion.cliente_id} 
                onChange={(e) => setFiltrosFacturacion((f) => ({ ...f, cliente_id: e.target.value }))}
              >
                <option value="">Todos</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre} {c.apellidos}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <button type="button" className="btn btn-secondary" onClick={loadReporteFacturacion}>
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {user && (user.oportunidades_habilitadas === true || user.oportunidades_habilitadas === 1) && tab === 'oportunidades' && (
        <div className="reportes-filters">
          <div className="filters-row">
            <div className="filter-group">
              <label>Fecha desde</label>
              <input 
                type="date" 
                value={filtrosOportunidades.fecha_desde} 
                onChange={(e) => setFiltrosOportunidades((f) => ({ ...f, fecha_desde: e.target.value }))} 
              />
            </div>
            <div className="filter-group">
              <label>Fecha hasta</label>
              <input 
                type="date" 
                value={filtrosOportunidades.fecha_hasta} 
                onChange={(e) => setFiltrosOportunidades((f) => ({ ...f, fecha_hasta: e.target.value }))} 
              />
            </div>
            <div className="filter-group">
              <label>Estado</label>
              <select 
                value={filtrosOportunidades.estado} 
                onChange={(e) => setFiltrosOportunidades((f) => ({ ...f, estado: e.target.value }))}
              >
                <option value="">Todos</option>
                <option value="prospecto">Prospecto</option>
                <option value="contactado">Contactado</option>
                <option value="reunion">Reunión</option>
                <option value="propuesta">Propuesta</option>
                <option value="negociacion">Negociación</option>
                <option value="ganada">Ganada</option>
                <option value="perdida">Perdida</option>
                <option value="pausada">Pausada</option>
              </select>
            </div>
            <div className="filter-group">
              <button type="button" className="btn btn-secondary" onClick={loadReporteOportunidades}>
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p>Cargando reporte...</p>
      ) : reporte ? (
        <div className="reporte-content">
          {user && (user.facturacion_habilitada === true || user.facturacion_habilitada === 1) && tab === 'facturacion' && (
            <>
              <div className="reporte-resumen">
                <h3>Resumen</h3>
                <div className="resumen-grid">
                  <div className="resumen-item">
                    <div className="resumen-label">Total Facturado</div>
                    <div className="resumen-value">
                      {money(reporte.totales?.total_facturado || 0)}
                    </div>
                  </div>
                  <div className="resumen-item">
                    <div className="resumen-label">Base Imponible</div>
                    <div className="resumen-value">
                      {money(reporte.totales?.total_base || 0)}
                    </div>
                  </div>
                  <div className="resumen-item">
                    <div className="resumen-label">IVA</div>
                    <div className="resumen-value">
                      {money(reporte.totales?.total_iva || 0)}
                    </div>
                  </div>
                  <div className="resumen-item">
                    <div className="resumen-label">Cantidad Facturas</div>
                    <div className="resumen-value">{reporte.totales?.cantidad_facturas || 0}</div>
                  </div>
                </div>
              </div>
              
              <div className="reporte-tabla">
                <h3>Detalle de Facturas</h3>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Número</th>
                        <th>Fecha</th>
                        <th>Cliente</th>
                        <th>Base</th>
                        <th>IVA</th>
                        <th>Total</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reporte.facturas?.map((f) => (
                        <tr key={f.id}>
                          <td>{f.numero}</td>
                          <td>{f.fecha_emision ? new Date(f.fecha_emision).toLocaleDateString('es-ES') : '-'}</td>
                          <td>{f.cliente_nombre} {f.cliente_apellidos}</td>
                          <td className="valor-cell">{money(f.base_imponible)}</td>
                          <td className="valor-cell">{money(f.importe_iva)}</td>
                          <td className="valor-cell"><strong>{money(f.total)}</strong></td>
                          <td><span className={`estado-badge estado-${f.estado}`}>{f.estado}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {user && (user.oportunidades_habilitadas === true || user.oportunidades_habilitadas === 1) && tab === 'oportunidades' && (
            <>
              <div className="reporte-resumen">
                <h3>Métricas</h3>
                <div className="resumen-grid">
                  <div className="resumen-item">
                    <div className="resumen-label">Total Oportunidades</div>
                    <div className="resumen-value">{reporte.metricas?.total_oportunidades || 0}</div>
                  </div>
                  <div className="resumen-item">
                    <div className="resumen-label">Valor Total</div>
                    <div className="resumen-value">
                      {money(reporte.metricas?.valor_total || 0)}
                    </div>
                  </div>
                  <div className="resumen-item">
                    <div className="resumen-label">Valor Ponderado</div>
                    <div className="resumen-value">
                      {money(reporte.metricas?.valor_ponderado || 0)}
                    </div>
                  </div>
                </div>
                
                {reporte.metricas?.por_etapa && Object.keys(reporte.metricas.por_etapa).length > 0 && (
                  <div className="metricas-etapas">
                    <h4>Por Etapa</h4>
                    <div className="etapas-grid">
                      {Object.entries(reporte.metricas.por_etapa).map(([etapa, datos]) => (
                        <div key={etapa} className="etapa-item">
                          <div className="etapa-nombre">{etapa}</div>
                          <div className="etapa-datos">
                            <span>{datos.count} oportunidades</span>
                            <span>{money(datos.valor)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="reporte-tabla">
                <h3>Detalle de Oportunidades</h3>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Título</th>
                        <th>Cliente</th>
                        <th>Valor Est.</th>
                        <th>Prob.</th>
                        <th>Valor Pond.</th>
                        <th>Etapa</th>
                        <th>Prioridad</th>
                        <th>Fecha Cierre</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reporte.oportunidades?.map((o) => (
                        <tr key={o.id}>
                          <td>{o.titulo}</td>
                          <td>{o.cliente_nombre} {o.cliente_apellidos}</td>
                          <td className="valor-cell">{money(o.valor_estimado)}</td>
                          <td>{o.probabilidad}%</td>
                          <td className="valor-cell valor-ponderado">{money(o.valor_ponderado)}</td>
                          <td><span className={`estado-badge estado-${o.etapa}`}>{o.etapa}</span></td>
                          <td><span className={`prioridad-badge prioridad-${o.prioridad}`}>{o.prioridad}</span></td>
                          <td>{o.fecha_estimada_cierre ? new Date(o.fecha_estimada_cierre).toLocaleDateString('es-ES') : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <p className="empty-msg">No hay datos para mostrar</p>
      )}
    </div>
  );
}

export default Reportes;
